/* eslint-disable no-console */
/**
 * Централизованная система логирования
 * Заменяет использование console.log с дополнительными возможностями
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  FATAL = 4
}

export type LogEntry = {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  data?: unknown;
  error?: Error;
  userId?: string;
  sessionId?: string;
}

export type LoggerConfig = {
  minLevel: LogLevel;
  enableConsole: boolean;
  enableStorage: boolean;
  enableRemote: boolean;
  maxStorageEntries: number;
  context?: string;
}

class Logger {
  private config: LoggerConfig;
  private logs: LogEntry[] = [];
  private sessionId: string;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      minLevel: process.env.NODE_ENV === 'production' ? LogLevel.WARN : LogLevel.DEBUG,
      enableConsole: true,
      enableStorage: true,
      enableRemote: process.env.NODE_ENV === 'production',
      maxStorageEntries: 1000,
      ...config
    };

    this.sessionId = this.generateSessionId();
    this.initializeLogger();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeLogger(): void {
    // Инициализация логгера
    if (typeof window === 'undefined') return; // Не выполнять в SSR

    if (this.config.enableStorage) {
      this.loadStoredLogs();
    }

    // Обработка необработанных ошибок
    window.addEventListener('error', (event) => {
      this.error('Unhandled error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    });

    // Обработка необработанных Promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled promise rejection', {
        reason: event.reason,
        promise: event.promise
      });
    });
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.minLevel;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    data?: unknown,
    error?: Error
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: this.config.context,
      data,
      error,
      userId: this.getCurrentUserId(),
      sessionId: this.sessionId
    };
  }

  private getCurrentUserId(): string | undefined {
    if (typeof localStorage === 'undefined') return undefined;
    // Получаем ID пользователя из хранилища или контекста
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id;
      }
    } catch {
      // Игнорируем ошибки парсинга
    }
    return undefined;
  }

  private logToConsole(entry: LogEntry): void {
    if (!this.config.enableConsole) return;

    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    const prefix = `[${LogLevel[entry.level]}] ${timestamp}`;
    const contextStr = entry.context ? `[${entry.context}]` : '';
    const message = `${prefix} ${contextStr} ${entry.message}`;
    const payload = entry.data as unknown as object;

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(message, payload);
        break;
      case LogLevel.INFO:
        console.info(message, payload);
        break;
      case LogLevel.WARN:
        console.warn(message, payload);
        break;
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(message, payload, entry.error);
        break;
    }
  }

  private storeLog(entry: LogEntry): void {
    if (!this.config.enableStorage || typeof localStorage === 'undefined') return;

    this.logs.push(entry);

    // Ограничиваем количество сохраненных логов
    if (this.logs.length > this.config.maxStorageEntries) {
      this.logs = this.logs.slice(-this.config.maxStorageEntries);
    }

    // Сохраняем в localStorage
    try {
      localStorage.setItem('app-logs', JSON.stringify(this.logs));
    } catch {
      // Если localStorage переполнен, очищаем старые логи
      this.logs = this.logs.slice(-500);
      try {
        localStorage.setItem('app-logs', JSON.stringify(this.logs));
      } catch (error) {
        console.error('Failed to store logs:', error);
      }
    }
  }

  private loadStoredLogs(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const storedLogs = localStorage.getItem('app-logs');
      if (storedLogs) {
        this.logs = JSON.parse(storedLogs);
      }
    } catch (error) {
      console.warn('Failed to load stored logs:', error);
      this.logs = [];
    }
  }

  private sendToRemote(entry: LogEntry): void {
    if (!this.config.enableRemote) return;

    // Отправляем критические ошибки на сервер
    if (entry.level >= LogLevel.ERROR) {
      // Здесь можно добавить отправку на сервер логирования
      // например, Sentry, LogRocket, или собственный API
      this.sendLogToServer(entry).catch(error => {
        console.error('Failed to send log to server:', error);
      });
    }
  }

  private async sendLogToServer(entry: LogEntry): Promise<void> {
    // Заглушка для отправки логов на сервер
    // В реальном проекте здесь будет API вызов
    if (process.env.NODE_ENV === 'development') {
      console.log('Would send to server:', entry);
    }
  }

  private log(level: LogLevel, message: string, data?: unknown, error?: Error): void {
    if (!this.shouldLog(level)) return;

    const entry = this.createLogEntry(level, message, data, error);

    this.logToConsole(entry);
    this.storeLog(entry);
    this.sendToRemote(entry);
  }

  /**
   * Логирование отладочной информации
   */
  debug(message: string, data?: unknown): void {
    this.log(LogLevel.DEBUG, message, data);
  }

  /**
   * Логирование информационных сообщений
   */
  info(message: string, data?: unknown): void {
    this.log(LogLevel.INFO, message, data);
  }

  /**
   * Логирование предупреждений
   */
  warn(message: string, data?: unknown): void {
    this.log(LogLevel.WARN, message, data);
  }

  /**
   * Логирование ошибок
   */
  error(message: string, data?: unknown, error?: Error): void {
    this.log(LogLevel.ERROR, message, data, error);
  }

  /**
   * Логирование критических ошибок
   */
  fatal(message: string, data?: unknown, error?: Error): void {
    this.log(LogLevel.FATAL, message, data, error);
  }

  /**
   * Создание логгера с контекстом
   */
  createContextLogger(context: string): Logger {
    return new Logger({
      ...this.config,
      context: this.config.context ? `${this.config.context}.${context}` : context
    });
  }

  /**
   * Получение всех логов
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Получение логов по уровню
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Получение логов по временному диапазону
   */
  getLogsByTimeRange(startTime: Date, endTime: Date): LogEntry[] {
    const start = startTime.toISOString();
    const end = endTime.toISOString();
    return this.logs.filter(log => log.timestamp >= start && log.timestamp <= end);
  }

  /**
   * Очистка логов
   */
  clearLogs(): void {
    this.logs = [];
    if (this.config.enableStorage && typeof localStorage !== 'undefined') {
      localStorage.removeItem('app-logs');
    }
  }

  /**
   * Экспорт логов в JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Установка минимального уровня логирования
   */
  setMinLevel(level: LogLevel): void {
    this.config.minLevel = level;
  }

  /**
   * Получение статистики логирования
   */
  getLogStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    
    Object.values(LogLevel).forEach(level => {
      if (typeof level === 'number') {
        stats[LogLevel[level]] = this.logs.filter(log => log.level === level).length;
      }
    });

    return stats;
  }
}

// Создаем глобальный экземпляр логгера
export const logger = new Logger();

// Экспортируем функции для удобства использования
export const log = {
  debug: (message: string, data?: unknown): void => logger.debug(message, data),
  info: (message: string, data?: unknown): void => logger.info(message, data),
  warn: (message: string, data?: unknown): void => logger.warn(message, data),
  error: (message: string, data?: unknown, error?: Error): void => logger.error(message, data, error),
  fatal: (message: string, data?: unknown, error?: Error): void => logger.fatal(message, data, error),
  createContext: (context: string): Logger => logger.createContextLogger(context)
};

export default logger;
